import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip, Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faMicrophone,
  faMicrophoneSlash,
  faHeadphones,
  faPhoneSlash,
  faClock,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import {
  CallBarContainer,
  LeftSection,
  StatusIndicator,
  ContactInfo,
  ContactAvatar,
  ContactDetails,
  CenterSection,
  TimerDisplay,
  RightSection,
  ActionButton,
  EndCallButton,
  Divider
} from './styled';
import VoiceCallService from '../../services/VoiceCallService';

// Format phone number for display
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX for US numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  // Return original if can't format
  return phone;
};

/**
 * ActiveCallBar - A fixed bottom bar that appears during active calls
 * Shows call status, contact info, timer, and action buttons
 */
const ActiveCallBar = ({
  visible = false,
  callData = null,
  contact = null,
  fromNumber = '',
  toNumber = '',
  callType = 'personal', // 'personal' or 'ai-agent'
  agentName = null,
  onMuteToggle,
  onEndCall,
  onContactClick
}) => {
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [callStatus, setCallStatus] = useState('initiating'); // initiating, ringing, connected
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Sync status from callData
  useEffect(() => {
    if (callData?.status) {
      if (callData.status === 'in-progress') {
        setCallStatus('connected');
      } else if (callData.status === 'ringing') {
        setCallStatus('ringing');
      } else if (callData.status === 'initiating') {
        setCallStatus('initiating');
      }
    }
  }, [callData?.status]);

  // Timer effect - use startTime from callData
  useEffect(() => {
    let interval = null;
    const startTime = callData?.startTime;
    
    if (visible && startTime) {
      // Calculate initial duration
      setDuration(Math.floor((Date.now() - startTime) / 1000));
      
      interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [visible, callData?.startTime]);

  // Get available audio input devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        setAudioDevices(audioInputs);
        if (audioInputs.length > 0 && !selectedDevice) {
          setSelectedDevice(audioInputs[0].deviceId);
        }
      } catch (error) {
        console.error('Error getting audio devices:', error);
      }
    };
    if (visible) {
      getDevices();
    }
  }, [visible, selectedDevice]);

  // Reset state when call ends
  useEffect(() => {
    if (!visible) {
      setDuration(0);
      setIsMuted(false);
      setCallStatus('initiating');
    }
  }, [visible]);

  // Format duration as MM:SS
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Get status label
  const getStatusLabel = () => {
    switch (callStatus) {
      case 'connected':
        return 'In Call';
      case 'ringing':
        return 'Ringing';
      case 'initiating':
        return 'Connecting';
      default:
        return 'Call';
    }
  };

  // Get contact initials
  const getInitials = () => {
    if (contact?.name) {
      const parts = contact.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    return <FontAwesomeIcon icon={faUser} />;
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    setIsMuted(prev => !prev);
    if (onMuteToggle) onMuteToggle(!isMuted);
  };

  // Handle end call
  const handleEndCall = async () => {
    try {
      if (callData?.callSid) {
        await VoiceCallService.endCall(callData.callSid);
      }
      if (onEndCall) onEndCall();
    } catch (error) {
      console.error('Error ending call:', error);
      if (onEndCall) onEndCall(); // Still close the bar
    }
  };

  // Microphone selector menu
  const microphoneMenuItems = audioDevices.length === 0
    ? [{ key: 'none', label: 'No microphones found', disabled: true }]
    : audioDevices.map(device => ({
        key: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
      }));

  if (!visible) return null;

  const content = (
    <CallBarContainer>
      <LeftSection>
        {/* Status Badge */}
        <StatusIndicator $status={callStatus}>
          <span className="status-dot" />
          {getStatusLabel()}
        </StatusIndicator>

        {/* Contact Info */}
        <ContactInfo>
          <ContactAvatar onClick={onContactClick} style={{ cursor: onContactClick ? 'pointer' : 'default' }}>
            {callType === 'ai-agent' ? (
              <FontAwesomeIcon icon={faRobot} />
            ) : (
              getInitials()
            )}
          </ContactAvatar>
          <ContactDetails>
            <span className="contact-name">
              {contact?.name || 'Unknown Caller'}
              {callType === 'ai-agent' && agentName && (
                <span style={{ fontWeight: 400, color: '#a0a0a0', marginLeft: 6, fontSize: 12 }}>
                  via {agentName}
                </span>
              )}
            </span>
            <span className="contact-numbers">
              {formatPhoneNumber(fromNumber)}
              <span className="arrow">→</span>
              {formatPhoneNumber(toNumber)}
            </span>
          </ContactDetails>
        </ContactInfo>
      </LeftSection>

      <CenterSection>
        <TimerDisplay>
          <FontAwesomeIcon icon={faClock} />
          {formatDuration(duration)}
        </TimerDisplay>
      </CenterSection>

      <RightSection>
        {/* Contact/Profile */}
        <Tooltip title="View Contact">
          <ActionButton onClick={onContactClick}>
            <FontAwesomeIcon icon={faUser} />
          </ActionButton>
        </Tooltip>

        {/* AI Agent indicator (if applicable) */}
        {callType === 'ai-agent' && (
          <Tooltip title="AI Agent Call">
            <ActionButton className="active">
              <FontAwesomeIcon icon={faRobot} />
            </ActionButton>
          </Tooltip>
        )}

        <Divider />

        {/* Microphone Selector */}
        <Dropdown
          menu={{
            items: microphoneMenuItems,
            selectable: true,
            selectedKeys: selectedDevice ? [selectedDevice] : [],
            onClick: ({ key }) => {
              setSelectedDevice(key);
              console.log('Selected microphone:', key);
              // TODO: Switch microphone on Twilio Device if needed
            },
          }}
          trigger={['click']}
          placement="topCenter"
        >
          <Tooltip title="Select Microphone">
            <ActionButton>
              <FontAwesomeIcon icon={faHeadphones} />
            </ActionButton>
          </Tooltip>
        </Dropdown>

        {/* Mute */}
        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
          <ActionButton 
            onClick={handleMuteToggle}
            className={isMuted ? 'muted' : ''}
          >
            <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} />
          </ActionButton>
        </Tooltip>

        {/* End Call */}
        <Tooltip title="End Call">
          <EndCallButton onClick={handleEndCall}>
            <FontAwesomeIcon icon={faPhoneSlash} />
          </EndCallButton>
        </Tooltip>
      </RightSection>
    </CallBarContainer>
  );

  // Render via portal
  return ReactDOM.createPortal(content, document.body);
};

export default ActiveCallBar;